'use client';
import cn from 'classnames';
import { ChangeEvent, FocusEvent } from 'react';
import { useFormikContext } from 'formik';

import Text from '@/components/common/Text/Text';
import type { ReferralCopyProps } from '@/components/common/SignUpApp/SignUpApp';

import { ApplicationFormValues } from './applicationValidation';
import './ReferralCodeField.style.scss';

type ReferralCodeFieldProps = {
  className?: string;
  label?: string;
  referralCopy?: ReferralCopyProps;
  /**
   * `field` — the labelled "REFERRAL CODE (OPTIONAL)" input used in My Info.
   * `summary` — the design's helper-text + underline input + UPDATE link used
   * in the Member Summary panel.
   */
  variant?: 'field' | 'summary';
  /** Helper text shown above the input in the `summary` variant. */
  helperText?: string;
  /** Action link copy in the `summary` variant, e.g. "Update". */
  actionLabel?: string;
  /** Pushes the committed code up so the preview cart can re-price. */
  onCommit: (referralCode: string) => void;
  /**
   * The code the feedback in `referralCopy` actually describes — i.e. the one
   * that was committed and priced. The message only renders while the input
   * still holds that code, so it cannot linger over a cleared or half-retyped
   * field.
   */
  appliedCode?: string;
  'data-testid'?: string;
};

/**
 * The referral code appears twice on the page — once in "My Info" and once in
 * the Member Summary panel — and the Figma confirms both. Both instances bind
 * to the *same* Formik `referralCode` value, so the two inputs are one value
 * kept in sync both ways.
 *
 * (In the multi-page flow the step 1 referral input sat outside Formik and was
 * tracked in ad-hoc local state; here it is a normal managed field.)
 */
function ReferralCodeField(props: ReferralCodeFieldProps) {
  const {
    className = '',
    label = 'Referral Code (optional)',
    referralCopy,
    variant = 'field',
    helperText = 'Enter your referral code',
    actionLabel = 'Update',
    onCommit,
    appliedCode,
    'data-testid': dataTestId
  } = props;

  const { values, setFieldValue } = useFormikContext<ApplicationFormValues>();

  const entered = (values.referralCode || '').trim();
  const message =
    !!entered && entered === (appliedCode || '').trim()
      ? referralCopy
      : undefined;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFieldValue('referralCode', event.currentTarget.value.toUpperCase());
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    const referralCode = event.currentTarget.value.toUpperCase();

    setFieldValue('referralCode', referralCode);
    onCommit(referralCode);
  };

  if (variant === 'summary') {
    return (
      <div className={cn('ReferralSummaryField', className)}>
        <Text
          variant='text-m'
          className='ReferralSummaryField-helper'
        >
          {helperText}
        </Text>
        <div className='ReferralSummaryField-row'>
          <input
            type='text'
            className='ReferralSummaryField-input'
            value={values.referralCode || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            data-testid={dataTestId}
          />
          <button
            type='button'
            className='ReferralSummaryField-action cursor'
            onClick={() => onCommit((values.referralCode || '').toUpperCase())}
          >
            <Text variant='title-xxs-500-upper'>{actionLabel}</Text>
          </button>
        </div>
        {message?.copy && (
          <Text
            className={cn('ReferralSummaryField-message', {
              'ReferralSummaryField-success': message.successfullyApplied
            })}
            variant='text-s'
          >
            {message.copy}
          </Text>
        )}
      </div>
    );
  }

  return (
    <div className={cn('InputField', className)}>
      <label className='InputField-label'>{label}</label>
      <input
        type='text'
        className={cn('InputField-input', {
          success: message?.successfullyApplied
        })}
        placeholder='None'
        value={values.referralCode || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        data-testid={dataTestId}
      />
      {message?.copy && (
        <Text
          className={cn('InputField-message', {
            'InputField-success': message.successfullyApplied
          })}
          variant='text-s'
        >
          {message.copy}
        </Text>
      )}
    </div>
  );
}

export default ReferralCodeField;
